import { createContext, useContext, useEffect, useRef } from 'react';

const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

export function imageUrl(path) {
  if (!path) return '';
  const normalized = path.replace(/^~\//, '/');
  if (normalized.startsWith('http')) return normalized;
  const base = IMAGE_BASE.replace(/\/$/, '');
  return normalized.startsWith('/') ? `${base}${normalized}` : `${base}/${normalized}`;
}

export const SpeedContext = createContext({ speedMultiplier: 1, isTest: false, fastMode: false, setFastMode: () => {} });

export function useSpeedMultiplier() {
  return useContext(SpeedContext).speedMultiplier;
}

/**
 * Enfoca el input cuando aparece un paso de respuesta.
 * `autoFocus` solo actúa en el primer mount y falla al venir de un botón Continuar.
 * @param {boolean} active — hay input de texto visible
 * @param {string|number} stepKey — cambia al avanzar de paso (ej. seqIndex)
 */
export function useFocusOnStep(active, stepKey) {
  const ref = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const t = window.setTimeout(() => {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [active, stepKey]);
  return ref;
}

/**
 * Wrapper usado por todos los TestRun. Muestra header mínimo con progreso y botón salir.
 */
export default function TestLayout({ current, total, onExit, children }) {
  const { isTest, fastMode, setFastMode } = useContext(SpeedContext);

  return (
    <div className="min-h-screen bg-white text-slate-800 relative flex flex-col items-center justify-center px-6 py-12">
      <header className="absolute top-0 left-0 right-0 bg-slate-100 border-b border-slate-200 py-1 px-4 flex justify-between items-center z-10">
        <span className="text-slate-500 text-sm">
          {total > 0 ? `${current} / ${total}` : ''}
        </span>
        <div className="flex items-center gap-3">
          {isTest && (
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-200 border border-amber-900 text-amber-900">
              MODO TESTING
            </span>
          )}
          {isTest && (
            <button
              type="button"
              onClick={() => setFastMode((f) => !f)}
              className={`text-xs font-semibold px-2 py-1 rounded border cursor-pointer transition-colors ${
                fastMode
                  ? 'bg-amber-400 border-amber-500 text-amber-900 hover:bg-amber-300'
                  : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
              }`}
            >
              Tiempo: x10
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            className="text-slate-600 hover:text-slate-900 text-sm border-0 bg-transparent cursor-pointer"
          >
            Salir
          </button>
        </div>
      </header>
      {children}
    </div>
  );
}

/** Pantalla de conclusión / resultados compartida */
export function DoneScreen({ conclusiones, onRetry, onBack, children }) {
  return (
    <div className="min-h-screen bg-white text-slate-800 flex flex-col items-center justify-center p-6">
      <div className="max-w-3xl w-full text-center">
        {conclusiones && (
          <>
            <h2 className="text-2xl font-bold mb-4">Conclusión</h2>
            <p className="text-slate-600 mb-8 text-lg leading-relaxed">{conclusiones}</p>
          </>
        )}
        {children}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer"
            >
              QUIERO VOLVER A INTENTARLO
            </button>
          )}
          <button
            onClick={onBack}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer"
          >
            QUIERO VOLVER AL INICIO
          </button>
        </div>
      </div>
    </div>
  );
}

/** Botones Sí / No estándar */
export function YesNoButtons({ onYes, onNo }) {
  return (
    <div className="flex justify-center items-center gap-10 md:gap-14 mt-6">
      <button type="button" onClick={onYes} className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity" aria-label="Sí">
        <img src="/assets/img/Oui.png" alt="Sí" className="h-24 w-auto" />
      </button>
      <button type="button" onClick={onNo} className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity" aria-label="No">
        <img src="/assets/img/Non.png" alt="No" className="h-24 w-auto" />
      </button>
    </div>
  );
}

/** Botón continuar (tilde verde) */
export function ContinueButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity mt-8"
      aria-label="Continuar"
    >
      <img src="/assets/img/Ok-icon.png" alt="Continuar" className="h-20 w-auto" />
    </button>
  );
}

export function LoadingScreen() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <p className="text-slate-600">Cargando...</p>
    </div>
  );
}

export function ErrorScreen({ message, onBack }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
      <p className="text-red-600 mb-4">{message}</p>
      <button onClick={onBack} className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
        Volver
      </button>
    </div>
  );
}
